import React, {useCallback, useContext, useRef, useState, useEffect, useLayoutEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {createPortal} from 'react-dom';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';

import {Paper} from '@mui/material';
import {styled} from '@mui/material/styles';

import {BoardMenuItemTypes} from 'appRedux/actions/workflow/types';
import {REQUESTER_CASES_LIST} from 'appRedux/actions/admin';
import {RootReducer} from 'appRedux/reducers';
import {CalendarEventType, CalendarBoardRequestType} from 'appRedux/actions/admin/types';

import {AlertContext} from 'contexts/alert/context';
import {AdminContext} from 'contexts/admin/context';

import RequesterCaseDrawer from 'components/AgentScreenComponents/_caseBlock/RequesterCaseDrawer/RequesterCaseDrawer';

import {getTagColor} from 'pages/admin/updateForm/partials/FormTags/helper';

import {
    PARAMETER_CURRENT_BOARD,
    routes,
    PARAMETER_TAG,
    PARAMETER_OPTION,
    PARAMETER_FORM,
    PARAMETER_AGENT,
    PARAMETER_SEARCH,
    PARAMETER_LAST_CREATED,
    PARAMETER_LAST_UPDATED,
} from 'config/index';
import {getLanguageById} from 'helpers/translationsHelper';
import {WeekNumberCalculation} from '@fullcalendar/core';

const CalendarContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    height: 'calc(100vh - 120px)',
    '& .fc': {
        height: '100%',
        '--fc-button-active-bg-color': theme.palette.primary.main,
        '--fc-button-active-border-color': theme.palette.primary.main,
        '--fc-button-hover-bg-color': theme.palette.primary.light,
        '--fc-button-hover-border-color': theme.palette.primary.light,
        '--fc-today-bg-color': theme.palette.grey[100],
        '& .fc-toolbar-title': {
            fontSize: '1.2rem',
        },
        '& .fc-button.fc-button-primary': {
            textTransform: 'capitalize',
            height: '36px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
            },
            '&.fc-button-active': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
            },
            '&:focus': {
                boxShadow: 'none',
                outline: 'none',
            },
        },
        '& .fc-prev-button .fc-icon, & .fc-next-button .fc-icon': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1.2rem',
            lineHeight: '1.2rem',
            paddingBottom: '3px',
        },
        '& .fc-icon-chevron-left:before': {
            content: '"❮"',
        },
        '& .fc-icon-chevron-right:before': {
            content: '"❯"',
        },
        '& .fc-event': {
            cursor: 'pointer',
        },
        '& .fc-timegrid-slot': {
            height: '3em',
        },
        '& .fc-event-title': {
            fontWeight: 'bold',
        },
    },
}));

export interface EventFetchInfo {
    start: Date;
    end: Date;
    startStr: string;
    endStr: string;
}

export interface CalendarComponentProps {
    currentBoard: BoardMenuItemTypes;
}

export interface CalendarRequestParams {
    start: string;
    end: string;
    agent?: string;
    form?: string;
    search?: string;
    lastCreated?: string;
    lastUpdated?: string;
    tags?: string;
    options?: string;
}

const CalendarBoard: React.FC<CalendarComponentProps> = ({currentBoard}) => {
    const dispatch = useDispatch();
    const {uuid} = useParams<{uuid: string}>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {activeRow, setActiveRow, myOrganization} = useContext(AdminContext);
    const {showAlert} = useContext(AlertContext);
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevRequestRef = useRef<CalendarRequestParams | null>(null);
    const calendarRef = useRef<FullCalendar>(null);
    const [initialView, setInitialView] = useState<string>('timeGridWeek');
    const [viewOptions, setViewOptions] = useState<string>('timeGridWeek,timeGridDay,listWeek');
    const [navigationOptions, setNavigationOptions] = useState<string>('prev,next today');

    // Get search parameters from URL
    const searchParamsObj = {
        agent: searchParams.get(PARAMETER_AGENT) || undefined,
        form: searchParams.get(PARAMETER_FORM) || undefined,
        search: searchParams.get(PARAMETER_SEARCH) || undefined,
        lastCreated: searchParams.get(PARAMETER_LAST_CREATED) || undefined,
        lastUpdated: searchParams.get(PARAMETER_LAST_UPDATED) || undefined,
        tags: searchParams.get(PARAMETER_TAG) || undefined,
        options: searchParams.get(PARAMETER_OPTION) || undefined,
    };

    const {
        admin: {calendarEvents, languageList},
        profile: {profile},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const getRequesterCasesCalendarBoard = useCallback(
        (data: CalendarBoardRequestType) =>
            dispatch({type: REQUESTER_CASES_LIST.REQUEST_CALENDAR_BOARD, payload: data}),
        [dispatch],
    );

    const hasRequestParamsChanged = useCallback((current: CalendarRequestParams, prev: CalendarRequestParams) => {
        return Object.keys(current).some(
            key => current[key as keyof CalendarRequestParams] !== prev[key as keyof CalendarRequestParams],
        );
    }, []);

    const getCalendarEvents = useCallback(
        (
            info: EventFetchInfo,
            successCallback: (events: CalendarEventType[]) => void,
            failureCallback: (error: Error) => void,
        ) => {
            try {
                const currentRequest: CalendarRequestParams = {
                    start: info.startStr,
                    end: info.endStr,
                    ...searchParamsObj,
                };

                if (!prevRequestRef.current || hasRequestParamsChanged(currentRequest, prevRequestRef.current)) {
                    prevRequestRef.current = currentRequest;

                    getRequesterCasesCalendarBoard({
                        uuid: uuid || '',
                        start: info.startStr,
                        end: info.endStr,
                        ...searchParamsObj,
                        showAlert,
                    });
                }

                successCallback(
                    (calendarEvents || []).map(event => {
                        let formattedStart = event.start;
                        let formattedEnd = event.end;

                        if (event.date && typeof event.start === 'string' && typeof event.end === 'string') {
                            const dateStr = event.date; // "YYYY-MM-DD"
                            const startTime = event.start; // "HH:MM"
                            const endTime = event.end; // "HH:MM"

                            formattedStart = `${dateStr}T${startTime}:00`;
                            formattedEnd = `${dateStr}T${endTime}:00`;
                        }

                        return {
                            id: event.id,
                            title: event.title,
                            start: formattedStart,
                            end: formattedEnd,
                            backgroundColor: event.backgroundColor,
                            borderColor: event.borderColor,
                            allDay: event.allDay,
                            extendedProps: event.extendedProps,
                            textColor: getTagColor(event.backgroundColor || '#ffffff'), // Set text color based on background color
                        };
                    }),
                );
            } catch (error) {
                console.error('Error fetching events:', error);
                failureCallback(error as Error);
            }
        },
        [getRequesterCasesCalendarBoard, uuid, calendarEvents, showAlert, searchParamsObj, hasRequestParamsChanged],
    );

    useEffect(() => {
        return () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, []);

    useLayoutEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 600) {
                setInitialView('listWeek');
                setViewOptions('listWeek');
                setNavigationOptions('prev,next');
            } else if (window.innerWidth < 1000) {
                setInitialView('timeGridDay');
                setViewOptions('timeGridDay,listWeek');
                setNavigationOptions('prev,next today');
            } else {
                setInitialView('timeGridWeek');
                setViewOptions('timeGridWeek,timeGridDay,listWeek');
                setNavigationOptions('prev,next today');
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Update calendar view when initialView changes
    useEffect(() => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.changeView(initialView);
        }
    }, [initialView]);

    if (!myOrganization || !profile) return null;

    const language = getLanguageById(languageList, profile.languageId || myOrganization?.language || 0);

    const {weekCalc, firstDayOfWeek} = useMemo(() => {
        const languageCode = language?.name?.toLowerCase() || 'en';

        let weekCalc = 'local';
        let firstDayOfWeek = 0;

        if (['de', 'fr', 'es', 'it', 'ru', 'ua'].includes(languageCode)) {
            weekCalc = 'ISO';
            firstDayOfWeek = 1;
        }

        return {weekCalc: weekCalc as WeekNumberCalculation, firstDayOfWeek};
    }, [language]);

    return (
        <CalendarContainer elevation={2}>
            <FullCalendar
                ref={calendarRef}
                plugins={[timeGridPlugin, dayGridPlugin, listPlugin]}
                initialView={initialView}
                headerToolbar={{
                    left: navigationOptions,
                    center: 'title',
                    right: viewOptions,
                }}
                events={getCalendarEvents}
                eventClick={info => {
                    console.log(info);
                    const extendedProps = info.event.extendedProps || {};
                    const caseId = info.event.extendedProps?.caseId || null;
                    const formId = info.event.extendedProps?.formId || null;
                    if (!caseId || !formId) return;

                    if (clickTimeoutRef.current) {
                        clearTimeout(clickTimeoutRef.current);
                        clickTimeoutRef.current = null;

                        navigate(
                            `${routes.REQUESTER_CASE}/${caseId}/form/${formId}?${PARAMETER_CURRENT_BOARD}=${currentBoard?.uuid}`,
                            {
                                state: {
                                    extendedProps,
                                    fromCalendar: true,
                                },
                            },
                        );
                    } else {
                        setActiveRow(null);

                        clickTimeoutRef.current = setTimeout(() => {
                            setActiveRow(caseId);
                            clickTimeoutRef.current = null;
                        }, 200); // Wait for second click before firing single click
                    }
                }}
                weekNumberCalculation={weekCalc}
                firstDay={firstDayOfWeek}
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                allDaySlot={false}
                nowIndicator={true}
                dayMaxEvents={true}
                stickyHeaderDates={true}
            />
            {activeRow && createPortal(<RequesterCaseDrawer caseId={activeRow} />, document.body)}
        </CalendarContainer>
    );
};

export default CalendarBoard;
